import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type IWorkflowBaseTriggerRepository from '@repositories/workflowBaseTriggerRepository/IWorkflowBaseTriggerRepository';
import type {
  WorkflowBaseTriggerDTO,
  WorkflowBaseTriggersFilter,
} from '@repositories/workflowBaseTriggerRepository/workflowBaseTrigger.types';
import type { Page } from '@type/Page';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import { WorkflowBaseTriggerDTOModel } from '@repositories/workflowBaseTriggerRepository/models/WorkflowBaseTriggerDTOModel';

const triggerRepo = repository.get<IWorkflowBaseTriggerRepository>(KEYS.WORKFLOW_BASE_TRIGGER_REPOSITORY);

export const triggerQueryKey = {
  all: ['trigger'] as const,
  lists: () => [triggerQueryKey.all, 'list'] as const,
  list: (filter?: WorkflowBaseTriggersFilter) => [...triggerQueryKey.lists(), filter] as const,
  details: () => [...triggerQueryKey.all, 'detail'] as const,
  detail: (triggerId: string) => [...triggerQueryKey.details(), triggerId] as const,
};

export const useGetWorkflowBaseTriggers = (
  queries?: WorkflowBaseTriggersFilter,
  options?: Omit<
    UseQueryOptions<Page<WorkflowBaseTriggerDTO>, AxiosError, Page<WorkflowBaseTriggerDTOModel>>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const selectTriggers = useCallback(
    (data: Page<WorkflowBaseTriggerDTO>) => ({
      ...data,
      content: data.content?.map(dto => new WorkflowBaseTriggerDTOModel(dto)),
    }),
    [],
  );

  return useQuery<Page<WorkflowBaseTriggerDTO>, AxiosError, Page<WorkflowBaseTriggerDTOModel>>({
    queryKey: triggerQueryKey.list(queries),
    queryFn: () => triggerRepo.getList(queries),
    select: selectTriggers,
    ...options,
  });
};

export const useGetWorkflowBaseTrigger = (
  triggerId: string,
  options?: Omit<
    UseQueryOptions<WorkflowBaseTriggerDTO, AxiosError, WorkflowBaseTriggerDTOModel>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const selectTrigger = useCallback((data: WorkflowBaseTriggerDTO) => new WorkflowBaseTriggerDTOModel(data), []);

  return useQuery<WorkflowBaseTriggerDTO, AxiosError, WorkflowBaseTriggerDTOModel>({
    queryKey: triggerQueryKey.detail(triggerId),
    queryFn: () => triggerRepo.getOne(triggerId),
    select: selectTrigger,
    ...options,
  });
};
